import React, { useContext, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Spinner,
  Text,
  Box,
} from '@chakra-ui/react';
import { Formik, Form, useFormikContext } from 'formik';
import { useMutation, useQuery } from '@apollo/client';
import * as Yup from 'yup';
import get from 'lodash/get';

import SpinnerContext from '../../contexts/SpinnerContext';
import UserContext from '../../contexts/UserContext';
import Input from '../../components/Input';
import Select from '../../components/Select';
import { SEND_UPS_AP_PUSH_NOTIFICATION, GET_ALL_ACCESS_POINTS, GET_UPS_AP_NOTIFICATION_COUNT, AccessPointsForDropdownQuery } from './query';
import { useCustomToast } from '../../hooks/useCustomToast';

const receiverOptions = [
  { value: '', label: 'Bitte wählen...', disabled: true },
  { value: 'ACTIVE_UPS_AP', label: 'Aktive UPS AP' },
  { value: 'INACTIVE_UPS_AP', label: 'Inaktive UPS AP' },
  { value: 'SINGLE_UPS_AP', label: 'Einzelner UPS AP' },
  { value: 'NO_INBOUND_EVER', label: 'No Inbound EVER' },
  { value: 'NO_INBOUND_ATM', label: 'No Inbound ATM' },
  { value: 'NO_PICKUP_EVER', label: 'No Pickup EVER' },
  { value: 'AP_STATUS', label: 'AccessPoint im Status:' },
];

const statusOptions = [
  { value: '', label: 'Bitte wählen...', disabled: true },
  { value: 'CONFIRMED', label: 'Angenommen (CONFIRMED)' },
  { value: 'WAITLIST', label: 'Auf Warteliste (WAITLIST)' },
  { value: 'PENDING', label: 'Wartet auf Ausweisprüfung (PENDING)' },
  { value: 'DRAFT', label: 'Nicht beantragt (DRAFT)' },
  { value: 'REJECTED', label: 'Abgelehnt (REJECTED)' },
];

const UpsApNotificationSchema = Yup.object().shape({
  receiver: Yup.string().required('Du musst noch einen Empfänger auswählen.'),
  accessPointId: Yup.string().when('receiver', {
    is: (receiver: string) => receiver === 'SINGLE_UPS_AP',
    then: Yup.string().required('Du musst noch eine Access Point ID eingeben.'),
  }),
  accessPointStatus: Yup.string().when('receiver', {
    is: (receiver: string) => receiver === 'AP_STATUS',
    then: Yup.string().required('Du musst noch einen Status auswählen.'),
  }),
  body: Yup.string().required('Du musst noch diese Eingabe machen, mein junger Padavan.'),
  comment: Yup.string().required('Du musst noch diese Eingabe machen, mein junger Padavan.'),
});

interface NotificationCountDisplayProps {
  isOpen: boolean;
}

const NotificationCountDisplay: React.FC<NotificationCountDisplayProps> = ({ isOpen }) => {
  const { values } = useFormikContext<{
    receiver: string;
    accessPointStatus: string;
  }>();

  const shouldSkipQuery =
    !isOpen || !values.receiver || (values.receiver === 'AP_STATUS' && !values.accessPointStatus) || values.receiver === 'SINGLE_UPS_AP';

  const { data, loading } = useQuery<{ upsApPushNotificationCount: number }>(GET_UPS_AP_NOTIFICATION_COUNT, {
    variables: {
      receiver: values.receiver || 'ACTIVE_UPS_AP',
      accessPointStatus: values.receiver === 'AP_STATUS' ? values.accessPointStatus : null,
    },
    skip: shouldSkipQuery,
    fetchPolicy: 'network-only',
  });

  if (!values.receiver || values.receiver === 'SINGLE_UPS_AP') {
    return null;
  }

  if (values.receiver === 'AP_STATUS' && !values.accessPointStatus) {
    return null;
  }

  return (
    <Box mt="4" p="3" bg="gray.50" borderRadius="md">
      <Flex alignItems="center" justifyContent="center">
        {loading ? (
          <Spinner size="sm" mr="2" />
        ) : (
          <Text color="gray.900">
            Es werden Push Notes an{' '}
            <Text as="span" fontWeight="bold" color="brand.500">
              {data?.upsApPushNotificationCount ?? 0}
            </Text>{' '}
            {data?.upsApPushNotificationCount === 1 ? 'Access Point' : 'Access Points'} versendet
          </Text>
        )}
      </Flex>
    </Box>
  );
};

interface SendUpsApNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
}

const SendUpsApNotificationModal: React.FC<SendUpsApNotificationModalProps> = ({ isOpen, onClose, refetch }) => {
  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useContext(SpinnerContext);
  const [sendNotification] = useMutation(SEND_UPS_AP_PUSH_NOTIFICATION);
  const { data: accessPointsData, loading: accessPointsLoading } = useQuery<AccessPointsForDropdownQuery>(GET_ALL_ACCESS_POINTS, {
    skip: !isOpen,
  });
  const toast = useCustomToast();

  const accessPointOptions = useMemo(() => {
    if (!accessPointsData?.accessPoints) return [{ value: '', label: 'Bitte wählen...', disabled: true }];

    const options = accessPointsData.accessPoints.map((ap) => {
      const firstName = ap.dropPoint?.user?.firstName || '';
      const lastName = ap.dropPoint?.user?.lastName || '';
      const statusDot = ap.isActive ? '●' : '○';
      const label = `${ap.accessPointID || 'Keine AP ID'} (${firstName} ${lastName}) | ${ap.status} | ${statusDot}`;
      return { value: ap.id, label };
    });

    return [{ value: '', label: 'Bitte wählen...', disabled: true }, ...options];
  }, [accessPointsData]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Push Note an UPS AP senden</ModalHeader>

        <Formik
          initialValues={{
            receiver: '',
            accessPointId: '',
            accessPointStatus: '',
            title: '',
            subTitle: '',
            body: '',
            comment: '',
          }}
          validationSchema={UpsApNotificationSchema}
          onSubmit={async ({ receiver, accessPointId, accessPointStatus, title, subTitle, body, comment }) => {
            try {
              toggleSpinner(true);
              await sendNotification({
                variables: {
                  receiver,
                  accessPointId: accessPointId || null,
                  accessPointStatus: receiver === 'AP_STATUS' ? accessPointStatus : null,
                  title,
                  subTitle,
                  body,
                  comment,
                  internalUser: internalUser.id,
                },
              });
              await refetch();
              toast({
                title: 'Erfolg!',
                description: 'Die UPS Access Points wurden benachrichtigt.',
                status: 'success',
              });
            } catch (error) {
              console.log(error);
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                status: 'error',
              });
            }
            toggleSpinner(false);
            onClose();
          }}
        >
          {({ values }) => (
            <Form>
              <ModalBody>
                <Select name="receiver" label="Empfänger auswählen" options={receiverOptions} />

                {values.receiver === 'SINGLE_UPS_AP' ? (
                  accessPointsLoading ? (
                    <Flex justifyContent="center" py="4">
                      <Spinner size="sm" />
                    </Flex>
                  ) : (
                    <Select name="accessPointId" label="Access Point auswählen" options={accessPointOptions} />
                  )
                ) : null}

                {values.receiver === 'AP_STATUS' ? (
                  <Select name="accessPointStatus" label="Status auswählen" options={statusOptions} />
                ) : null}

                <Input name="title" label="Überschrift (optional)" />
                <Input name="subTitle" label="Zweite Überschrift (nur iOS & optional)" />
                <Input name="body" label="Nachricht" />
                <Input name="comment" label="Kommentar" />

                <NotificationCountDisplay isOpen={isOpen} />
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                  Schließen
                </Button>
                <Button ml="4" size="sm" colorScheme="brand" type="submit">
                  Senden
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default SendUpsApNotificationModal;
